/**
 * blocksStore.ts
 *
 * Raw read/write functions for the `blocks` object store.
 * No React, no API calls, no business logic — pure IndexedDB access.
 *
 * Mirrors every method that existed in blockContext:
 *   getBlock        → blocksStore.getBlock()
 *   getChildren     → blocksStore.getChildren()
 *   getChildrenBlocks → blocksStore.getChildrenBlocks()
 *   updateBlock     → blocksStore.updateBlock()  (called by mutations, not directly)
 *   loadBlocks      → blocksStore.bulkLoad()
 *   upsertBlocks    → blocksStore.upsertBlocks()
 *   addBlock        → blocksStore.addBlock()
 *   removeBlock     → blocksStore.removeBlock()
 *   hasBlock        → blocksStore.hasBlock()
 *   getAllBlocks     → blocksStore.getAllBlocks()
 */

import type { Block } from "@/types/block";
import { appDB } from "../db/client";
import type { BlockRecord } from "../db/schema";

/**
 * Converts a raw Block (from API) into a BlockRecord (for IndexedDB).
 * Adds sync fields with safe defaults.
 */
export function toBlockRecord(block: Block, isSync: 0 | 1 = 1): BlockRecord {
  const record: BlockRecord = {
    ...block,
    isSync,
    localUpdatedAt: Date.now(),
    syncAttempts: 0,
    syncError: null,
  };
  // An alive block can never legitimately carry trash stamps. Backends before
  // the restoreFromTrash $unset fix left stale deletedAt/deletedBy on restored
  // blocks — stripping here also heals rows already mirrored into IDB.
  if (record.status === "alive") {
    delete record.deletedAt;
    delete record.deletedBy;
  }
  return record;
}

/**
 * Converts a BlockRecord back to a plain Block (for passing to existing components).
 * Strips the IDB-only sync fields so existing code receives the shape it expects.
 */
export function toBlock(record: BlockRecord): Block {
  const { isSync, localUpdatedAt, syncAttempts, syncError, ...block } = record;
  return block as Block;
}

// ─── Single block reads ───────────────────────────────────────────

/** Get a single block by ID. Returns undefined if not found. */
export async function getBlock(blockId: string): Promise<BlockRecord | undefined> {
  return appDB.blocks.get(blockId);
}

/** Returns true if a block exists in IDB. */
export async function hasBlock(blockId: string): Promise<boolean> {
  const count = await appDB.blocks.where("_id").equals(blockId).count();
  return count > 0;
}

// ─── Children ────────────────────────────────────────────────────

/** Get child block IDs for a given parent block (from blockIds array). */
export async function getChildren(blockId: string): Promise<string[]> {
  const block = await appDB.blocks.get(blockId);
  return block?.blockIds ?? [];
}

/** Get full child BlockRecords for a given parent block. */
export async function getChildrenBlocks(blockId: string): Promise<BlockRecord[]> {
  const childIds = await getChildren(blockId);
  if (childIds.length === 0) return [];
  const records = await appDB.blocks.bulkGet(childIds);
  return records.filter((b): b is BlockRecord => b !== undefined);
}

// ─── Workspace-scoped reads ──────────────────────────────────────

/** Get all alive blocks for a workspace. */
export async function getAllBlocks(workspaceId: string): Promise<BlockRecord[]> {
  return appDB.blocks
    .where("[workspaceId+isSync]")
    .anyOf([
      [workspaceId, 0], // isSync false (Dexie stores booleans as 0/1 in indexes)
      [workspaceId, 1], // isSync true
    ])
    .and((b) => b.status === "alive")
    .toArray();
}

/** Get all page-type blocks for a workspace (used for sidebar). */
export async function getPageBlocks(workspaceId: string): Promise<BlockRecord[]> {
  return appDB.blocks
    .where("workspaceId")
    .equals(workspaceId)
    .and((b) => b.blockType === "page" && b.status === "alive")
    .toArray();
}

/** Get all blocks by parentId (direct children lookup). */
export async function getBlocksByParentId(parentId: string): Promise<BlockRecord[]> {
  return appDB.blocks
    .where("parentId")
    .equals(parentId)
    .and((b) => b.status === "alive")
    .toArray();
}

// ─── Writes ───────────────────────────────────────────────────────

/**
 * Add a single new block.
 * isSync defaults to false because new blocks are unsynced until watcher runs.
 */
export async function addBlock(block: Block, isSync: 0 | 1 = 0): Promise<void> {
  await appDB.blocks.put(toBlockRecord(block, isSync));
}

/**
 * Update specific fields on an existing block.
 * Always marks isSync: 0 | 1 = 0 and bumps localUpdatedAt.
 */
export async function updateBlock(blockId: string, updates: Partial<Block>): Promise<void> {
  const existing = await appDB.blocks.get(blockId);
  if (!existing) return;
  await appDB.blocks.put({
    ...existing,
    ...updates,
    _id: blockId, // ensure primary key never changes
    isSync: 0 as const,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Soft-delete a block (sets status = "deleted").
 * Hard deletes are never done from the client.
 */
export async function removeBlock(blockId: string): Promise<void> {
  const existing = await appDB.blocks.get(blockId);
  if (!existing) return;
  await appDB.blocks.put({
    ...existing,
    status: "deleted",
    deletedAt: new Date().toISOString(),
    isSync: 0 as const,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Bulk load blocks from the API response.
 * Used during initial hydration — marks all as isSync: 1 (already in MongoDB).
 */
export async function bulkLoad(blocks: Block[]): Promise<void> {
  const records = blocks.map((b) => toBlockRecord(b, 1));
  await appDB.blocks.bulkPut(records);
}

/**
 * Upsert multiple blocks — update if exists, add if not.
 * Mirrors blockContext.upsertBlocks(). Used when API returns fresh data.
 * Preserves isSync: 1 since data came from server.
 */
export async function upsertBlocks(blocks: Block[]): Promise<void> {
  const records = blocks.map((b) => toBlockRecord(b, 1));
  await appDB.blocks.bulkPut(records);
}

/**
 * Sync-ack — intentionally a NO-OP on the blocks table (Step 3 of
 * BLOCK_STORE_REDESIGN.md).
 *
 * Flipping `isSync: 1` here was the last UI-invisible write to the observed
 * `blocks` table: it re-fired every per-key liveQuery (editor header,
 * breadcrumb, publish modal) once per save, and a 200-row paste-ack once per
 * open observer. Block dirtiness is now derived from the syncQueue
 * (getPendingBlockIds in syncQueueStore): when SyncWatcher marks ops synced,
 * blocks drop out of the pending set automatically — there is nothing to
 * write back. The row-level `isSync` field is legacy: mutations still stamp
 * 0 alongside their content writes (inert), and nothing reads it anymore.
 *
 * Kept as exported functions so SyncWatcher's call sites continue to mark
 * the ack points in its flow.
 */
export async function markBlockSynced(_blockId: string): Promise<void> {
  // Intentionally empty — see doc comment above.
}

/** Bulk sync-ack — same intentional no-op as markBlockSynced. */
export async function markBlocksSynced(_blockIds: string[]): Promise<void> {
  // Intentionally empty — see doc comment above.
}

/**
 * Custom DOM event fired after `mergeServerStampedDatabaseProperties` writes
 * a server-authoritative value to IDB. The React block context listens for
 * this and updates its in-memory `Map<string, Block>` so subsequent edits
 * (e.g. an instant title rename that sends the whole block) carry the
 * stamped value back to the server instead of clobbering it with `undefined`.
 */
export const BLOCK_SERVER_STAMPED_EVENT = "block:server-stamped";

export interface BlockServerStampedDetail {
  blockId: string;
  uniqueId?: number;
  databaseProperties: Record<string, unknown>;
  /** When set, this property key is removed from `databaseProperties` after
   *  the new stamp is merged. Used by drag-between-datasources to clean up
   *  the orphaned mirror under the source datasource's propertyId. */
  unsetPropertyId?: string;
}

/**
 * Merge server-stamped fields (returned by `/api/note/block/batch-create`
 * or `/drag-and-drop`) into a local block.
 *
 * Writes BOTH the defensive canonical (`value.uniqueId`) and the mirror
 * under `value.databaseProperties[propertyId]`.
 *
 * Keeps `isSync: 1` because we are applying the server's authoritative
 * value, not staging a new edit — without this, the merge would re-enqueue
 * a sync op and overwrite the just-assigned number on the next round-trip.
 *
 * Also dispatches `BLOCK_SERVER_STAMPED_EVENT` so the React block context
 * can refresh its in-memory copy. Without this, a follow-up update (instant
 * title rename, etc.) would read the stale context block — missing the
 * stamp — and overwrite the server's value with `undefined` on the next
 * round-trip.
 *
 * `unsetPropertyId` is used by the drag-between-datasources flow: when a
 * page is moved into a new datasource it gets a new stamp under the new
 * propertyId, and the old propertyId's mirror should be removed.
 */
export async function mergeServerStampedDatabaseProperties(
  blockId: string,
  stamped: Record<string, unknown>,
  uniqueId?: number,
  unsetPropertyId?: string,
): Promise<void> {
  const hasStampedDbProps = stamped && Object.keys(stamped).length > 0;
  const hasUniqueId = typeof uniqueId === "number" && Number.isFinite(uniqueId);
  if (!hasStampedDbProps && !hasUniqueId) return;

  const existing = await appDB.blocks.get(blockId);
  if (!existing) return;
  const value =
    existing.value && typeof existing.value === "object"
      ? { ...(existing.value as Record<string, unknown>) }
      : {};
  if (hasStampedDbProps) {
    const currentProps =
      "databaseProperties" in value && value.databaseProperties && typeof value.databaseProperties === "object"
        ? { ...(value.databaseProperties as Record<string, unknown>) }
        : {};
    const nextProps: Record<string, unknown> = { ...currentProps, ...stamped };
    if (unsetPropertyId && unsetPropertyId in nextProps) {
      delete nextProps[unsetPropertyId];
    }
    value.databaseProperties = nextProps;
  }
  if (hasUniqueId) {
    value.uniqueId = uniqueId;
  }
  await appDB.blocks.put({
    ...existing,
    value,
    isSync: 1 as const,
    localUpdatedAt: Date.now(),
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<BlockServerStampedDetail>(BLOCK_SERVER_STAMPED_EVENT, {
        detail: {
          blockId,
          ...(hasUniqueId ? { uniqueId } : {}),
          databaseProperties: hasStampedDbProps ? stamped : {},
          ...(unsetPropertyId ? { unsetPropertyId } : {}),
        },
      }),
    );
  }
}

/**
 * Record a sync failure on a block.
 */
export async function recordBlockSyncFailure(blockId: string, error: string): Promise<void> {
  const existing = await appDB.blocks.get(blockId);
  if (!existing) return;
  await appDB.blocks.update(blockId, {
    syncAttempts: (existing.syncAttempts ?? 0) + 1,
    syncError: error,
  });
}

// NOTE (Step 3): the former getUnsyncedBlocks() query is gone. The row-level
// isSync flag is no longer maintained by sync-acks, so a flag-based query
// would return stale results. Use getPendingBlockIds() from syncQueueStore —
// the queue is the authoritative source of pending work.
